import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, AppLink, C, Callout } from "./primitives.js";

export const meta = {
  id: "distribute-article",
  title: "Distributing content across 8 channels",
  category: "Workflows",
  lastUpdated: "2026-05-23",
  description: "Per-channel walkthrough: Telegram, Brevo, YouTube, FB, IG, WhatsApp, X, TikTok. Heuristic captions, $0 LLM cost at send time.",
  relatedGuides: ["produce-article", "credentials-reference"],
  readTimeMin: 7,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        Once an article is produced, distribution is a click-per-channel: open the keyword's detail panel in <AppLink to="pipeline">Pipeline</AppLink>, click the <strong>Distribute ▾</strong> button, pick the channel + mode. Every channel supports a <strong>dry-run</strong> option to preview the payload before going live.
      </Lead>

      <Callout kind="tip" title="No LLM tokens at send time">
        Captions are built heuristically from the article H1 + first sentences + hashtags derived from the brief's <C>semanticMustCover</C>. Zero per-channel LLM spend. The expensive Claude work happened during Brief + Produce.
      </Callout>

      <H2>Channels at a glance</H2>
      <table className="my-3 w-full table-auto text-sm">
        <thead className="text-2xs uppercase tracking-wider text-muted">
          <tr><th className="px-2 py-1 text-left">Channel</th><th className="px-2 py-1 text-left">Asset</th><th className="px-2 py-1 text-left">Cost</th><th className="px-2 py-1 text-left">Setup time</th></tr>
        </thead>
        <tbody className="divide-y divide-line">
          <tr><td className="px-2 py-1">Telegram</td><td className="px-2 py-1">video &gt; photo &gt; text</td><td className="px-2 py-1">free</td><td className="px-2 py-1">5 min</td></tr>
          <tr><td className="px-2 py-1">Brevo email</td><td className="px-2 py-1">HTML template</td><td className="px-2 py-1">free tier or $25/mo</td><td className="px-2 py-1">10 min</td></tr>
          <tr><td className="px-2 py-1">YouTube</td><td className="px-2 py-1">horizontal.mp4 or short.mp4</td><td className="px-2 py-1">free</td><td className="px-2 py-1">~1h (OAuth)</td></tr>
          <tr><td className="px-2 py-1">Facebook</td><td className="px-2 py-1">photo + caption or text+link</td><td className="px-2 py-1">free</td><td className="px-2 py-1">~1-2h</td></tr>
          <tr><td className="px-2 py-1">Instagram</td><td className="px-2 py-1">square image (1:1) + caption</td><td className="px-2 py-1">free</td><td className="px-2 py-1">shares FB auth</td></tr>
          <tr><td className="px-2 py-1">WhatsApp</td><td className="px-2 py-1">text + link</td><td className="px-2 py-1">~$0.005-0.05/msg</td><td className="px-2 py-1">~2-3h + template review</td></tr>
          <tr><td className="px-2 py-1">X (Twitter)</td><td className="px-2 py-1">text + link, thread or single</td><td className="px-2 py-1">$200/mo</td><td className="px-2 py-1">~1h</td></tr>
          <tr><td className="px-2 py-1">TikTok</td><td className="px-2 py-1">short.mp4 (9:16)</td><td className="px-2 py-1">free</td><td className="px-2 py-1">~30 min</td></tr>
        </tbody>
      </table>

      <H2>Telegram</H2>
      <P>Free, easiest. Bot token + channel id. Auto-picks the best asset: video &gt; photo &gt; text. Caption ≤1024 chars (media) or ≤4096 (text). Supports silent posts. Use this first when validating your pipeline.</P>

      <H2>Brevo (email)</H2>
      <P>Three modes:</P>
      <Ul>
        <Li><strong>Test send</strong> — single recipient, useful for previewing. Pick this first.</Li>
        <Li><strong>Transactional</strong> — single recipient, real send.</Li>
        <Li><strong>Campaign</strong> — list-based broadcast. Use the Master Subscriber List (487 contacts, see Audience page).</Li>
      </Ul>
      <P>Templates already wired: <C>article-announcement.ts</C> + <C>weekly-digest.ts</C>. Both Hebrew-aware, RTL-rendered, brand-palette inlined HTML (no MJML / React-email deps).</P>

      <H2>YouTube</H2>
      <P>
        Uses the external <C>shmool-youtube-mcp</C> spawned via stdio. Defaults to <C>private</C> visibility — you review on YouTube Studio before publishing.
      </P>
      <Ul>
        <Li><strong>Dry run</strong> — previews title (from <C>article.meta.title</C> ≤100 chars), description (meta + URL + AlternaBe footer), tags (keyword + cluster + brief mustCover + brand).</Li>
        <Li><strong>Upload (private)</strong> — real upload. ~30s-2min depending on video size.</Li>
      </Ul>
      <P>Picks horizontal.mp4 first (long-form), falls back to short.mp4 (auto-tagged <C>#shorts</C>). Hero.png becomes the thumbnail when channel is verified.</P>

      <H2>Facebook</H2>
      <P>
        Page post via Graph API. Two modes:
      </P>
      <Ul>
        <Li><strong>Photo</strong> (default when hero.png exists) — multipart upload + caption ≤400 chars + article URL.</Li>
        <Li><strong>Text</strong> — message + link. FB auto-fetches the OG preview card from your article URL.</Li>
      </Ul>

      <H2>Instagram</H2>
      <P>
        Same auth as Facebook (linked IG Business account). 2-step container/publish flow handled inside the orchestrator. Caption ≤2200 chars (we cap at 2000). 5-12 auto-generated hashtags.
      </P>
      <Callout kind="warn" title="IG requires a PUBLIC image URL">
        Instagram can't fetch from your laptop — it requires a publicly reachable URL. The Distribute dropdown prompts you for the URL on live send. Easiest: paste the Sanity CDN URL of the hero image.
      </Callout>

      <H2>WhatsApp Business</H2>
      <P>Two paths picked per send:</P>
      <Ul>
        <Li><strong>Free-form</strong> — plain text. Only deliverable to recipients within the 24h customer-service window (i.e. they messaged you in the last 24h). Best for groups where members are active.</Li>
        <Li><strong>Template</strong> — approved Meta template. The only path for broadcasts outside the window. Requires <C>WHATSAPP_ANNOUNCEMENT_TEMPLATE</C> registered + approved (~24h Meta review).</Li>
      </Ul>
      <P>Two communities ("clients+therapists" / "therapists-only"), each with separate Phone Number ID + default recipient list.</P>

      <H2>X (Twitter)</H2>
      <P>OAuth 1.0a (long-lived, no refresh-token rotation). Two modes:</P>
      <Ul>
        <Li><strong>Thread</strong> — lead tweet with 🧵 + numbered body tweets sliced from first paragraphs + final tweet with "המאמר המלא: \&lt;URL\&gt;". Each tweet ≤280 chars. Chain-aborts on first failure.</Li>
        <Li><strong>Single</strong> — H1 + URL in one tweet (~250 chars).</Li>
      </Ul>
      <Callout kind="warn">
        Requires X API v2 Basic tier ($200/mo). Free tier is read-only and can't post. <strong>Critical setup gotcha</strong>: log in as the AlternaBe brand account before clicking "Generate Access Token" — the token is tied to whoever is logged in at that moment.
      </Callout>

      <H2>TikTok</H2>
      <P>OAuth 2.0 with auto-refreshing access tokens. Privacy mode picked per-post:</P>
      <Ul>
        <Li><strong>SELF_ONLY</strong> — sandbox-safe, no app review needed. Posts visible only to your account. Use this until production review approves.</Li>
        <Li><strong>MUTUAL_FOLLOW_FRIENDS</strong> + <strong>PUBLIC_TO_EVERYONE</strong> — require TikTok app review (~1-2 weeks).</Li>
      </Ul>
      <P>Uses <C>data/videos/&lt;slug&gt;/short.mp4</C> (9:16, what the video-gen pipeline already produces). 3-call posting flow (init → upload → poll status). The route auto-persists refreshed access tokens to .env so daily rotation is invisible to you.</P>

      <H2>The full cascade in one keyword session</H2>
      <P>
        For a single article you'd typically: Telegram first (cheapest, free, validates the flow) → Brevo test send to yourself → Brevo campaign to Master List → FB + IG → X thread → TikTok. Each click is independent — no state across channels, no rollback needed if one fails.
      </P>
    </>
  );
}

export const guide: Guide = { meta, Body };
